import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { userSelector } from './selectors';
import { displayNumber } from './utils';
import { DAY_MS, HOUR_MS, MINUTE_MS, SECOND_MS } from './time';
import { useRadarProfileData } from './slices/radarUserProfile';
import { useRadarConfig } from './slices/radarConfig';
import { cardsItemsSelector } from './slices/cards';

export const useBalanceTradingCampaignBalance = (campaignCards) => {
  const cards = useSelector(cardsItemsSelector);
  return useMemo(() => {
    if (!campaignCards) {
      return null;
    }
    const currencyCardsMap = {};
    campaignCards.forEach((card) => {
      currencyCardsMap[card.card] = card;
    });
    return Object.values(cards).reduce(
      (acc, card) => {
        const balanceCard = currencyCardsMap[card.cardTemplate._id];
        if (
          card &&
          !card.reward &&
          !card.disabled &&
          !card.shredded &&
          balanceCard
        ) {
          acc.value += balanceCard.value;
          acc.cardCount += 1;
        }
        return acc;
      },
      { cardCount: 0, value: 0 },
    );
  }, [campaignCards, cards]);
};

export const useShredTradingCampaignBalance = (campaignCards) => {
  const cards = useSelector(cardsItemsSelector);
  return useMemo(() => {
    if (!campaignCards) {
      return 0;
    }
    const currencyCardsMap = {};
    campaignCards.forEach((cardId) => {
      currencyCardsMap[cardId] = true;
    });

    return Object.values(cards).filter(
      (card) =>
        card &&
        !card.reward &&
        !card.disabled &&
        !card.shredded &&
        card.cardTemplate &&
        currencyCardsMap[card.cardTemplate._id],
    ).length;
  }, [campaignCards, cards]);
};

export function useForceUpdate() {
  const [, updateState] = useState();
  return useCallback(() => updateState({}), []);
}

export const useRefreshTimer = (interval, condition) => {
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    if (!condition) {
      const timeToInterval =
        interval - ((Date.now() - new Date().setHours(0, 0, 0, 0)) % interval);
      let intervalId;
      const timeoutId = setTimeout(() => {
        // eslint-disable-next-line max-nested-callbacks
        intervalId = setInterval(() => forceUpdate(), interval);
        forceUpdate();
      }, timeToInterval);
      return () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
    }
    return undefined;
  }, [condition, forceUpdate, interval]);
};

export const useTimer = (
  finishTime,
  { normalize = false, interval = SECOND_MS } = {},
) => {
  const difference = finishTime && +new Date(finishTime) - +new Date();
  const ended = !difference || difference < 0;
  useRefreshTimer(interval, ended);

  const times = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  if (!ended) {
    times.days = difference / DAY_MS;
    times.hours = (difference / HOUR_MS) % 24;
    times.minutes = (difference / MINUTE_MS) % 60;
    times.seconds = (difference / SECOND_MS) % 60;

    Object.keys(times).forEach((key) => {
      times[key] = Math.floor(times[key]);
      if (normalize) {
        times[key] = displayNumber(times[key]);
      }
    });
  }

  times.ended = ended;
  return times;
};

const userDataDefaults = {
  about: '',
  playStyles: [],
  games: [],
  platforms: [],
  languages: [],
  recommendationSetup: {
    worldwide: true,
    showAge: false,
    showGender: false,
    show: true,
  },
};

export const useRadarProfileDataWithDefaults = (language) => {
  const { userData, loading: userDataLoading } = useRadarProfileData();
  const { config, loading: configLoading } = useRadarConfig();
  const { birthday, country, gender, username } = useSelector(userSelector);
  const loading = userDataLoading || configLoading;

  const { avatars = [] } = config;

  const [avatar, setAvatar] = useState(
    avatars[Math.floor(Math.random() * avatars.length)],
  );

  useEffect(
    () => setAvatar(avatars[Math.floor(Math.random() * avatars.length)]),
    [avatars],
  );

  const userDataWithDefaults = useMemo(
    () => ({
      ...userDataDefaults,
      nickname: username,
      gender: gender?.toUpperCase().charAt(0),
      country,
      birthYear: new Date(birthday)?.getFullYear(),
      languages: [language],
      avatar,
      ...userData,
    }),
    [birthday, country, gender, language, userData, username, avatar],
  );

  return {
    loading,
    userData: userDataWithDefaults,
    config,
  };
};
